import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [currentStore, setCurrentStoreState] = useState(null);
  const [impersonating, setImpersonating] = useState(!!localStorage.getItem("tb_platform_token"));

  const loadStores = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/my-stores");
      setStores(data.stores || []);
      const savedId = localStorage.getItem("tb_store");
      const found = (data.stores || []).find((s) => s.id === savedId);
      if (found) setCurrentStoreState(found);
      else if ((data.stores || []).length === 1) {
        setCurrentStoreState(data.stores[0]);
        localStorage.setItem("tb_store", data.stores[0].id);
      }
      return data.stores || [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("tb_token");
      if (!token) { setLoading(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
        await loadStores();
      } catch {
        localStorage.removeItem("tb_token");
      } finally {
        setLoading(false);
      }
    })();
  }, [loadStores]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("tb_token", data.access_token);
      setUser(data.user);
      await loadStores();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register-company", payload);
      if (data.pending) {
        return { ok: true, pending: true, message: data.message };
      }
      localStorage.setItem("tb_token", data.access_token);
      setUser(data.user);
      await loadStores();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const logout = () => {
    localStorage.removeItem("tb_token");
    localStorage.removeItem("tb_store");
    localStorage.removeItem("tb_platform_token");
    setUser(null);
    setStores([]);
    setCurrentStoreState(null);
    setImpersonating(false);
  };

  const impersonate = async (companyId) => {
    const { data } = await api.post(`/platform/companies/${companyId}/impersonate`);
    if (!localStorage.getItem("tb_platform_token")) {
      localStorage.setItem("tb_platform_token", localStorage.getItem("tb_token"));
    }
    localStorage.setItem("tb_token", data.access_token);
    localStorage.removeItem("tb_store");
    setImpersonating(true);
    setUser(data.user);
    await loadStores();
    return data;
  };

  const stopImpersonation = async () => {
    const pt = localStorage.getItem("tb_platform_token");
    if (!pt) return;
    localStorage.setItem("tb_token", pt);
    localStorage.removeItem("tb_platform_token");
    localStorage.removeItem("tb_store");
    setImpersonating(false);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      await loadStores();
    } catch { /* ignore */ }
  };

  const setCurrentStore = (store) => {
    setCurrentStoreState(store);
    if (store) localStorage.setItem("tb_store", store.id);
  };

  return (
    <AuthContext.Provider value={{ user, loading, stores, currentStore, setCurrentStore, login, register, logout, loadStores, impersonate, stopImpersonation, impersonating }}>
      {children}
    </AuthContext.Provider>
  );
}
